import React from 'react';
import {ErrorState} from '@stripe-internal/connect-embedded-lib';
import {Button} from '@sail/ui';
import {message, useIntl} from '@sail/intl';
import {useSubmerchantAuthentication} from './SubmerchantAuthenticationContext';
import {useEmbeddedInvalidOrExpiredSession} from '../connect/EmbeddedInvalidOrExpiredSessionContext';
import {usePlatformFrameContext} from './PlatformFrameContext/PlatformFrameContext';

export const messages = {
  sessionHasBeenInvalidatedTitle: message({
    id: 'connectEmbed.accountOnboarding.sessionHasBeenInvalidated.title',
    defaultMessage: 'Session expired or invalidated',
    description:
      'Title message shown to the user indicating that their session is expired or no longer valid',
  }),
  sessionHasBeenInvalidatedSubtitle: message({
    id: 'connectEmbed.accountOnboarding.sessionHasBeenInvalidated.subtitle',
    defaultMessage:
      'You session has expired or been invalidated. Please refresh the page.',
    description:
      'Subtitle explaning the session has been expired or invalidated',
  }),
  sessionHasBeenInvalidatedAction: message({
    id: 'connectEmbed.accountOnboarding.sessionHasBeenInvalidated.action',
    defaultMessage: 'Refresh',
    description:
      'Refresh button displayed when the session expires or is invalidated',
  }),
};

export const SubmerchantInvalidSessionCheck: React.FC<{
  requiresAuthentication: boolean;
}> = ({requiresAuthentication, children}): JSX.Element | null => {
  const {isApiKeyOverride} = useSubmerchantAuthentication();
  const {isSessionInvalidOrExpired} = useEmbeddedInvalidOrExpiredSession();
  const intl = useIntl();

  // If this component requires auth, the session is invalid, and auth is not supported - we will render UI asking the user to refresh
  // When does this happen? In hosted onboarding, if the session is invalidated (for example, if the user logs in to another account in another tab)
  const shouldRenderSessionInvalidUI =
    requiresAuthentication && isSessionInvalidOrExpired && isApiKeyOverride;

  const {reloadWindow} = usePlatformFrameContext();

  if (shouldRenderSessionInvalidUI) {
    return (
      <ErrorState
        title={intl.formatMessage(messages.sessionHasBeenInvalidatedTitle)}
        description={intl.formatMessage(
          messages.sessionHasBeenInvalidatedSubtitle,
        )}
        actions={[
          <Button
            type="primary"
            onPress={() => {
              // Note: window.location.reload won't work in the UI layer frame, so we use the platformframecontext message to do so in the platform frame
              reloadWindow();
            }}
          >
            {intl.formatMessage(messages.sessionHasBeenInvalidatedAction)}
          </Button>,
        ]}
      />
    );
  }

  return <>{children}</>;
};
